import { Plan, PlanDefinition, PlanMeta } from "./types";
import { PLANS } from "./information";
import { cloneDeep } from "lodash";

// Super super legacy plans
const PLAN_ALIASES: Record<string, Plan> = {
    free: Plan.Public, // Before Public Plan
    payg: Plan.Enterprise,
    pro: Plan.Enterprise,
    custom: Plan.Enterprise
};

function resolvePlanKey(plan: Plan | string | null | undefined): Plan {
    if (plan === null || plan === undefined) {
        return Plan.UninitalizedWorkspace;
    }

    if (PLANS[plan as Plan]) {
        return plan as Plan;
    }

    const aliasKey = String(plan).toLowerCase();
    return PLAN_ALIASES[aliasKey] || Plan.Legacy;
}

/**
 * Get the metadata for a plan (UI strings, icons)
 */
export function getPlanMeta(plan: Plan | string): PlanMeta {
    const resolvedPlan = resolvePlanKey(plan);
    const planDef = PLANS[resolvedPlan] || PLANS[Plan.Legacy];
    return (
        planDef.meta || {
            title: "Legacy Plan",
            description: "",
            tagline: "",
            icon: "far fa-scroll-old",
            support: "Priority Support"
        }
    );
}

export function getIconForPlan(plan: Plan | string): string {
    return getPlanMeta(plan).icon;
}

export function getTitleForPlan(plan: Plan | string, isTrial: boolean = false): string {
    if (isTrial || plan === Plan.UbpPremiumTrial) {
        return getPlanMeta(Plan.UbpPremiumTrial).title;
    }
    return getPlanMeta(plan).title;
}

export function getDescriptionForPlan(plan: Plan | string): string {
    return getPlanMeta(plan).description;
}

export function getTaglineForPlan(plan: Plan | string): string {
    return getPlanMeta(plan).tagline;
}

export function getSupportForPlan(plan: Plan | string): string {
    return getPlanMeta(plan).support;
}

export function isCustomEnterprisePlan(plan: Plan | string): boolean {
    return (
        plan &&
        [Plan.Enterprise, Plan.EnterpriseGrowth, "payg", "pro", "custom"].includes(plan as string)
    );
}

export function isFreePlan(plan: Plan | string): boolean {
    return [Plan.Research, Plan.LegacySandbox, Plan.Public, Plan.Legacy, "free", "", null].includes(
        plan as any
    );
}

export function isPaidPlan(plan: Plan | string): boolean {
    return !isFreePlan(plan);
}

export function isEnterprisePlan(plan: Plan | string): boolean {
    const resolvedPlan = resolvePlanKey(plan);
    return [Plan.EnterpriseGrowth, Plan.Enterprise].includes(resolvedPlan);
}

export function isUbpPlan(plan: Plan | string): boolean {
    const resolvedPlan = resolvePlanKey(plan);
    return [Plan.UbpBasic, Plan.UbpCore, Plan.UbpGrowth].includes(resolvedPlan);
}

export function isSelfServePaidPlan(plan: Plan | string): boolean {
    return [Plan.UbpGrowth, Plan.UbpCore, Plan.UbpBasic, Plan.LegacyStarter].includes(plan as Plan);
}

export function isSeatAddonPlan(plan: Plan | string): boolean {
    const resolvedPlan = resolvePlanKey(plan);
    return [Plan.UbpCore, Plan.LegacyStarter].includes(resolvedPlan);
}

export function isTrialPlan(usage: { plan: string; trial?: { isTrial: boolean } }): boolean {
    const isStarterTrial = usage?.plan === Plan.LegacyStarter && usage?.trial?.isTrial;
    return usage?.plan === Plan.UbpPremiumTrial || isStarterTrial;
}

export function isFreeOrTrialPlan(usage: { plan: string; trial?: { isTrial: boolean } }): boolean {
    return isFreePlan(usage?.plan) || isTrialPlan(usage);
}

export function getWorkspaceSettingsForPlan(
    plan: Plan | string,
    isCreditSystemEnabled: boolean = false,
    researchPlanOption: any = null
): PlanDefinition {
    const resolvedPlan = resolvePlanKey(plan);
    let planDef = cloneDeep(PLANS[resolvedPlan] || PLANS[Plan.UninitalizedWorkspace]);

    // Apply runtime overrides based on parameters
    if (isCreditSystemEnabled) {
        planDef.features.creditSystem = true;
    }

    // Specific logic for Research plan customization
    if (resolvedPlan === Plan.Research && researchPlanOption) {
        const planLimits = {
            trainingCredits: { sources: 20000, transforms: 100000, trainingCredits: 25 },
            images: { sources: 100000, transforms: 500000, trainingCredits: 5 }
        };

        const selectedLimits = planLimits[researchPlanOption] ?? {
            sources: 10000,
            transforms: 50000,
            trainingCredits: 3
        };

        if (isCreditSystemEnabled) {
            selectedLimits.sources = 100000; // 100k
            selectedLimits.transforms = 1000000; // 1M
        }

        planDef.limit.sources = selectedLimits.sources;
        planDef.limit.transforms = selectedLimits.transforms;
        planDef.train.quota = selectedLimits.trainingCredits;
        planDef.research = { selectedPlan: researchPlanOption, startDate: new Date() };
    }

    // Feature flag overrides for plan changes
    // Replaces featureFlagOverridesForPlanChange
    if (resolvedPlan === Plan.LegacySandbox || resolvedPlan === Plan.Public) {
        planDef.features.modelEvaluation = false;
    } else if (resolvedPlan === Plan.LegacyStarter || resolvedPlan === Plan.EnterpriseGrowth) {
        planDef.features.classAttributes = true;
        planDef.features.manageSourceVideo = true;
    }

    if (resolvedPlan === Plan.Enterprise) {
        planDef.features.modelEvaluation = true;
        planDef.features.classAttributes = true;
        planDef.features.manageSourceVideo = true;
        planDef.features.deviceManagerV2 = true;
    }

    return planDef;
}

export function getPricingTablePlans(
    currentPlan?: Plan | string | null,
    exclude: Plan[] = []
): Plan[] {
    const resolvedCurrentPlan = currentPlan ? resolvePlanKey(currentPlan) : null;
    const visiblePlans: Plan[] = [];
    const excludePlans = exclude.map((plan) => resolvePlanKey(plan));

    // Discover all plans that should be visible
    for (const plan of Object.values(Plan)) {
        if (plan === null || plan === Plan.UninitalizedWorkspace || plan === Plan.Legacy) {
            continue;
        }

        const resolvedPlan = resolvePlanKey(plan);

        // Skip excluded plans
        if (excludePlans.includes(resolvedPlan)) {
            continue;
        }

        const planDef = PLANS[resolvedPlan];
        const planMeta = planDef?.meta;
        const isVisible = planMeta?.visibleOnPricingTable === true;

        // Always include visible plans
        if (isVisible) {
            visiblePlans.push(resolvedPlan);
        }
        // Include delisted plans only if user is currently on them
        else if (resolvedCurrentPlan === resolvedPlan) {
            visiblePlans.push(resolvedPlan);
        }
    }

    return visiblePlans;
}
